import { Button, Textbox, useScanner, useValidationResultDialog } from '@nest/uikit-react';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTerminalClient } from '../client-rpc-router';
import { StorageLocationSelectDialog } from './StorageLocationSelectDialog';

interface StorageLocationScanInputProps {
	value?: any;
	onChange?: (value: any) => void;
	placeholder?: string;
	autoFocus?: boolean;
	disabled?: boolean;
}

export function StorageLocationScanInput({ value, onChange, placeholder, autoFocus, disabled }: StorageLocationScanInputProps) {
	const client = useTerminalClient();
	const scanner = useScanner();
	const [code, setCode] = useState<string>('');
	const [isEditing, setIsEditing] = useState<boolean>(!value);

	const scanLocationMutation = useMutation({
		mutationFn: (code: string) => client.wms.getWmsCodeInfoQuery.execute({ code }),
		onSuccess: result => {
			if (result.location) {
				onChange?.(result.location);
				setCode('');
				setIsEditing(false);
			}
		}
	});

	useEffect(() => {
		if (isEditing && scanner.code && !disabled) {
			handleScan(scanner.code);
		}
	}, [scanner.code, isEditing, disabled]);

	useEffect(() => {
		setIsEditing(!value);
	}, [value]);

	const handleScan = (scannedCode: string) => {
		setCode(scannedCode);
		scanLocationMutation.mutate(scannedCode);
	};

	useValidationResultDialog([scanLocationMutation.error]);

	// view/edit handled inline; submit handler not needed

	const [isDialogOpen, setIsDialogOpen] = useState(false);

	return (
		<div className="flex gap-2 items-center w-full">
			{!isEditing && value ? (
				<div className="flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2">
					<div className="text-nowrap text-ellipsis overflow-hidden">{value?.name}</div>
					<Button type="button" variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
						Změnit
					</Button>
				</div>
			) : (
				<>
					<Textbox
						value={code}
						onChange={e => setCode(e.target.value)}
						placeholder={placeholder || 'Naskenujte lokaci...'}
						autoFocus={autoFocus}
						disabled={disabled || scanLocationMutation.isPending}
						onKeyDown={e => {
							if (e.key === 'Enter') {
								e.preventDefault();
								handleScan(code);
							}
						}}
					/>
					<Button type="button" variant="outline" onClick={() => setIsDialogOpen(true)} disabled={disabled}>
						Vybrat
					</Button>
				</>
			)}

			<StorageLocationSelectDialog
				open={isDialogOpen}
				onOpenChange={setIsDialogOpen}
				onSelect={loc => {
					onChange?.(loc);
					setIsDialogOpen(false);
					setIsEditing(false);
				}}
			/>
		</div>
	);
}
